import React, { useCallback, useState } from 'react';
import {
  Box,
  Flex,
  Icon,
  Modal,
  ModalOverlay,
  ModalContent,
  ModalHeader,
  ModalBody,
  ModalCloseButton,
  Text,
  VStack,
  useColorModeValue,
  Progress,
  IconButton,
  SimpleGrid,
} from '@chakra-ui/react';
import { useDropzone } from 'react-dropzone';
import { FiUpload, FiX, FiFile, FiMusic, FiImage } from 'react-icons/fi';
import { useMedia } from '../../../contexts/MediaContext';

interface MediaUploaderProps {
  isOpen: boolean;
  onClose: () => void;
  journalEntryId?: string;
  onUploadComplete: () => void;
}

interface UploadingFile {
  file: File;
  progress: number;
  error?: string;
}

const MediaUploader: React.FC<MediaUploaderProps> = ({
  isOpen,
  onClose,
  journalEntryId,
  onUploadComplete,
}) => {
  const [uploadingFiles, setUploadingFiles] = useState<UploadingFile[]>([]);
  const { uploadMedia } = useMedia();
  const dropzoneColor = useColorModeValue('gray.100', 'gray.700');
  const borderColor = useColorModeValue('gray.300', 'gray.600');

  const onDrop = useCallback(async (acceptedFiles: File[]) => {
    const newFiles = acceptedFiles.map(file => ({
      file,
      progress: 0,
    }));

    setUploadingFiles(prev => [...prev, ...newFiles]);

    if (!journalEntryId) {
      return;
    }

    // Upload files sequentially
    for (const fileData of newFiles) {
      try {
        await uploadMedia(fileData.file, journalEntryId);
        setUploadingFiles(prev =>
          prev.map(f =>
            f.file === fileData.file ? { ...f, progress: 100 } : f
          )
        );
      } catch (error) {
        setUploadingFiles(prev =>
          prev.map(f =>
            f.file === fileData.file
              ? { ...f, error: 'Upload failed' }
              : f
          )
        );
      }
    }

    onUploadComplete();
  }, [journalEntryId, uploadMedia, onUploadComplete]);

  const { getRootProps, getInputProps, isDragActive } = useDropzone({
    onDrop,
    accept: {
      'image/*': ['.png', '.jpg', '.jpeg', '.gif'],
      'audio/*': ['.mp3', '.wav'],
      'application/pdf': ['.pdf'],
      'application/msword': ['.doc'],
      'application/vnd.openxmlformats-officedocument.wordprocessingml.document': ['.docx'],
    },
  });

  const removeFile = (file: File) => {
    setUploadingFiles(prev => prev.filter(f => f.file !== file));
  };

  const getFileIcon = (file: File) => {
    if (file.type.startsWith('image/')) return FiImage;
    if (file.type.startsWith('audio/')) return FiMusic;
    return FiFile;
  };

  return (
    <Modal isOpen={isOpen} onClose={onClose} size="xl">
      <ModalOverlay />
      <ModalContent>
        <ModalHeader>Upload Files</ModalHeader>
        <ModalCloseButton />
        <ModalBody pb={6}>
          <VStack spacing={4}>
            <Box
              {...getRootProps()}
              w="full"
              h="200px"
              border="2px dashed"
              borderColor={isDragActive ? 'blue.400' : borderColor}
              borderRadius="md"
              bg={dropzoneColor}
              p={4}
              cursor="pointer"
              transition="all 0.2s"
              _hover={{
                borderColor: 'blue.400',
              }}
            >
              <input {...getInputProps()} />
              <Flex
                height="100%"
                direction="column"
                align="center"
                justify="center"
              >
                <Icon as={FiUpload} w={8} h={8} mb={2} />
                <Text textAlign="center">
                  {isDragActive
                    ? 'Drop the files here...'
                    : 'Drag and drop files here, or click to select files'}
                </Text>
                <Text fontSize="sm" color="gray.500" mt={2}>
                  Supports images, audio, and documents
                </Text>
              </Flex>
            </Box>

            {uploadingFiles.length > 0 && (
              <SimpleGrid columns={{ base: 1, md: 2 }} spacing={4} w="full">
                {uploadingFiles.map(({ file, progress, error }) => (
                  <Box
                    key={file.name}
                    p={3}
                    border="1px solid"
                    borderColor={borderColor}
                    borderRadius="md"
                  >
                    <Flex align="center" mb={2}>
                      <Icon as={getFileIcon(file)} mr={2} />
                      <Text flex="1" fontSize="sm" isTruncated>
                        {file.name}
                      </Text>
                      <IconButton
                        icon={<FiX />}
                        aria-label="Remove file"
                        size="sm"
                        variant="ghost"
                        onClick={() => removeFile(file)}
                      />
                    </Flex>
                    {error ? (
                      <Text color="red.500" fontSize="sm">
                        {error}
                      </Text>
                    ) : (
                      <Progress
                        value={progress}
                        size="sm"
                        colorScheme={progress === 100 ? 'green' : 'blue'}
                      />
                    )}
                  </Box>
                ))}
              </SimpleGrid>
            )}
          </VStack>
        </ModalBody>
      </ModalContent>
    </Modal>
  );
};

export default MediaUploader; 